import { useState } from "react";
import axios from "axios";
import { Upload, FileText, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  company_name: "Unternehmen *",
  contact_name: "Ansprechpartner",
  contact_email: "E-Mail",
  contact_phone: "Telefon",
  address: "Straße / Adresse",
  city: "Stadt",
  website: "Website",
  google_maps_url: "Google Maps URL",
  message: "Nachricht / Anfrage",
  notes: "Notizen",
  category: "Kategorie (→ wird zu Tag)",
};

export default function CSVImportWizard({ open, onClose, users, stages, onDone }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [assignedTo, setAssignedTo] = useState("");
  const [stageId, setStageId] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep(1); setFile(null); setParsed(null); setMapping({}); setAssignedTo(""); setStageId(""); setBusy(false); };
  const close = () => { reset(); onClose(); };

  const onFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await axios.post(`${API}/leads/import/preview`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setParsed(r.data);
      setMapping(r.data.suggested_mapping || {});
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "CSV konnte nicht geparst werden");
    } finally { setBusy(false); }
  };

  const handleMappingChange = (field, csvHeader) => {
    setMapping(m => ({ ...m, [field]: csvHeader === "_none" ? undefined : csvHeader }));
  };

  const buildRows = () => {
    if (!parsed) return [];
    return parsed.rows_preview.map(row => {
      const out = {};
      Object.entries(mapping).forEach(([field, header]) => {
        if (header && row[header] !== undefined) {
          out[field] = String(row[header]);
        }
      });
      return out;
    });
  };

  // For commit, we need to re-fetch full data — but our preview returns up to 1000 rows in rows_preview (we limited to 10).
  // Adjust: re-upload file in commit step? Simpler: use rows_preview for commit since backend caps at 1000 anyway.
  // Better solution: send file again with mapping. For Sprint 1B keep simple: full preview returns all parsed rows (up to 1000).
  // → Let's refetch with full preview before commit.
  const handleCommit = async () => {
    if (!mapping.company_name) { toast.error("Bitte wenigstens 'Unternehmen' zuordnen"); return; }
    setBusy(true);
    try {
      // Re-parse file to get ALL rows (preview returned only 10)
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/leads/import/preview`, fd, { withCredentials: true });
      const allRows = r.data.rows_all || r.data.rows_preview || [];

      const mapped = allRows.map(row => {
        const out = {};
        Object.entries(mapping).forEach(([field, header]) => {
          if (!header) return;
          const v = row[header];
          if (v === undefined || v === null) return;
          out[field] = String(v).trim();
        });
        return out;
      }).filter(r => r.company_name);

      const commit = await axios.post(`${API}/leads/import`, {
        rows: mapped,
        assigned_to: assignedTo || null,
        stage_id: stageId || null,
        source: "csv_import",
      }, { withCredentials: true });
      toast.success(`${commit.data.inserted} Leads importiert (${commit.data.skipped} übersprungen)`);
      onDone?.();
      close();
    } catch (err) { toast.error(err.response?.data?.detail || "Import fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid="csv-import-modal">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold flex items-center gap-2"><Upload size={16} /> CSV-Import</DialogTitle>
        </DialogHeader>

        {/* Stepbar */}
        <div className="flex items-center gap-2 text-xs">
          {[
            { n: 1, l: "Datei wählen" },
            { n: 2, l: "Spalten zuordnen" },
            { n: 3, l: "Importieren" },
          ].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2 flex-1">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step >= s.n ? "bg-foreground text-background" : "bg-muted text-muted-foreground"}`}>{step > s.n ? <Check size={11} /> : s.n}</span>
              <span className={`font-semibold ${step >= s.n ? "text-foreground" : "text-muted-foreground"}`}>{s.l}</span>
              {i < 2 && <span className="flex-1 h-px bg-border" />}
            </div>
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="py-6">
            <label htmlFor="csv-file" className="block border-2 border-dashed border-border rounded-sm p-10 text-center cursor-pointer hover:border-foreground transition-colors" data-testid="csv-dropzone">
              <FileText size={32} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium">CSV oder Excel-Datei wählen</p>
              <p className="text-xs text-muted-foreground mt-1">.csv · .xlsx · UTF-8 / cp1252 · max. 5.000 Zeilen</p>
              {file && <p className="text-xs text-foreground mt-2 font-mono">{file.name}</p>}
              <input id="csv-file" type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFileSelect} className="hidden" data-testid="csv-file-input" />
            </label>
            {busy && <p className="text-xs text-muted-foreground text-center mt-3">Parse...</p>}
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && parsed && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm">
                <strong>{parsed.rows_total}</strong> Zeilen erkannt · {parsed.delimiter === "xlsx" ? <code className="bg-muted px-1 rounded text-xs">Excel-Datei</code> : <>Trennzeichen <code className="bg-muted px-1 rounded text-xs">{parsed.delimiter === "," ? "," : parsed.delimiter === ";" ? "Semikolon" : parsed.delimiter}</code></>}
              </p>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto border border-border rounded-sm p-3 bg-muted/20">
              {parsed.lead_fields.map(field => (
                <div key={field} className="grid grid-cols-[140px_auto_1fr] items-center gap-3">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">{FIELD_LABELS[field] || field}</Label>
                  <ArrowRight size={12} className="text-muted-foreground" />
                  <Select value={mapping[field] || "_none"} onValueChange={(v) => handleMappingChange(field, v)}>
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`mapping-${field}`}>
                      <SelectValue placeholder="— nicht zuordnen —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— nicht zuordnen —</SelectItem>
                      {parsed.headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Zuweisen an</Label>
                <Select value={assignedTo || "_unassign"} onValueChange={v => setAssignedTo(v === "_unassign" ? "" : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="csv-assignee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_unassign">— nicht zugewiesen —</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Start-Stage</Label>
                <Select value={stageId || "_default"} onValueChange={v => setStageId(v === "_default" ? "" : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="csv-stage"><SelectValue placeholder="Standard (Neu)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_default">Standard (Neu)</SelectItem>
                    {stages.filter(s => !s.is_won && !s.is_lost).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-between gap-2">
              <Button type="button" variant="outline" onClick={() => { setStep(1); setParsed(null); }} className="rounded-sm">Zurück</Button>
              <Button onClick={() => setStep(3)} disabled={!mapping.company_name} className="rounded-sm gap-1.5" data-testid="csv-next-btn">Weiter <ArrowRight size={13} /></Button>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && parsed && (
          <div className="space-y-4">
            <div className="bg-muted/30 border border-border rounded-sm p-4">
              <p className="text-sm">
                <strong>{parsed.rows_total}</strong> Zeilen werden importiert.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Owner: {assignedTo ? users.find(u => u.id === assignedTo)?.name || users.find(u => u.id === assignedTo)?.email : "nicht zugewiesen"}
              </p>
              <p className="text-xs text-muted-foreground">Start-Stage: {stageId ? stages.find(s => s.id === stageId)?.name : "Neu"}</p>
            </div>
            <div className="border border-border rounded-sm overflow-x-auto max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/30 sticky top-0">
                  <tr>{Object.entries(mapping).filter(([, h]) => h).map(([f]) => <th key={f} className="px-2 py-2 text-left uppercase tracking-wide text-[10px] font-bold text-muted-foreground">{f.replace("_", " ")}</th>)}</tr>
                </thead>
                <tbody>
                  {buildRows().slice(0, 10).map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      {Object.entries(mapping).filter(([, h]) => h).map(([f]) => <td key={f} className="px-2 py-1.5 truncate max-w-[150px]">{r[f] || "—"}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between gap-2">
              <Button type="button" variant="outline" onClick={() => setStep(2)} className="rounded-sm">Zurück</Button>
              <Button onClick={handleCommit} disabled={busy} className="rounded-sm gap-1.5" data-testid="csv-commit-btn">
                <Upload size={13} /> {busy ? "Importiere..." : "Jetzt importieren"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
